import React, { useState } from 'react';
import { FolderKanban, CheckCircle2 } from 'lucide-react';
import ContentEditor from './ContentEditor';

const PROJECT_CATEGORIES = [
  'Eğitim & Sosyal Sorumluluk', 'Burs & Eğitim Desteği', 'Kız Çocukları',
  'Topluluk Bağışı', 'Gençlik', 'Kırsal Eğitim', 'Ulusal Egemenlik',
  'Kültür Turları', 'Online Kültür', 'Ödül & Teşvik',
  'Ruh Sağlığı', 'Sağlık & Eğitim', 'Avrupa Birliği',
  'Dijital Okuryazarlık', 'Yetişkin Eğitimi', 'Pandemi Dayanışma', 'Çalıştay',
];

const ONGOING_SCHEMA = [
  { key: 'title', label: 'Başlık', type: 'text', required: true, placeholder: 'Örn: Anadolu\'ya Armağan Projesi' },
  { key: 'category', label: 'Kategori', type: 'text', placeholder: 'Eğitim & Sosyal Sorumluluk', suggestions: PROJECT_CATEGORIES },
  { key: 'year', label: 'Yıl', type: 'text', placeholder: '2026', suggestions: ['2026', '2025', '2024', '2023'] },
  { key: 'date', label: 'Tarih (gösterim için)', type: 'text', placeholder: 'Mayıs 2026' },
  { key: 'image', label: 'Kapak Görseli', type: 'image' },
  { key: 'excerpt', label: 'Özet (kartta görünen)', type: 'textarea', placeholder: 'Projeniz hakkında 1-2 cümlelik özet...' },
  { key: 'video_id', label: 'YouTube ID (varsa)', type: 'text', placeholder: 'Örn: SeXWvU5VWO4 — video için YouTube video ID' },
  { key: 'type', label: 'Tip (image|video)', type: 'text', placeholder: 'image', suggestions: ['image', 'video'] },
  { key: 'content', label: 'Detay İçeriği', type: 'richtext', placeholder: 'Projenin tam hikâyesi, hedefler, faydalanıcılar, galeri... Başlık, liste, alıntı, görsel ekleyebilirsiniz.' },
  { key: 'featured', label: 'Öne Çıkan? (sayfa üstündeki büyük kart olur)', type: 'bool' },
];

const COMPLETED_SCHEMA = [
  { key: 'title', label: 'Başlık', type: 'text', required: true, placeholder: 'Örn: Pandemi 100 Tıp Projesi' },
  { key: 'category', label: 'Kategori', type: 'text', placeholder: 'Sağlık & Eğitim', suggestions: PROJECT_CATEGORIES },
  { key: 'year', label: 'Tamamlanma Yılı', type: 'text', placeholder: '2023', suggestions: ['2024', '2023', '2022', '2021', '2020'] },
  { key: 'date', label: 'Tarih', type: 'text', placeholder: 'Temmuz 2023' },
  { key: 'image', label: 'Kapak Görseli', type: 'image' },
  { key: 'excerpt', label: 'Özet (kartta görünen)', type: 'textarea', placeholder: 'Projenin tamamlanmasıyla elde edilen sonuçlar...' },
  { key: 'beneficiaries', label: 'Faydalanıcı Sayısı (opsiyonel)', type: 'text', placeholder: 'Örn: 500+ öğrenci' },
  { key: 'budget', label: 'Bütçe / Kaynak (opsiyonel)', type: 'text', placeholder: 'Örn: ₺250.000' },
  { key: 'video_id', label: 'YouTube ID (varsa)', type: 'text', placeholder: 'Örn: SeXWvU5VWO4 — video için YouTube video ID' },
  { key: 'type', label: 'Tip (image|video)', type: 'text', placeholder: 'image', suggestions: ['image', 'video'] },
  { key: 'content', label: 'Detay İçeriği', type: 'richtext', placeholder: 'Proje hikâyesi, elde edilen sonuçlar, fotoğraflar, teşekkürler...' },
];

export default function AdminProjects() {
  const [tab, setTab] = useState('ongoing');

  return (
    <div data-testid="admin-projects-tabs">
      <div className="mb-6">
        <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Projeler</div>
        <h1 className="font-display text-4xl mt-2 leading-none">Proje Yönetimi</h1>
        <p className="mt-2 text-ink-400 text-sm">Devam eden ve tamamlanan projelerinizi ayrı ayrı yönetin.</p>
      </div>

      <div className="flex gap-2 mb-6 bg-cream-200 p-1.5 rounded-2xl border border-ink-100 inline-flex">
        <button
          onClick={() => setTab('ongoing')}
          data-testid="tab-ongoing"
          className={`px-5 h-11 rounded-xl text-sm font-semibold transition-all inline-flex items-center gap-2 ${
            tab === 'ongoing' ? 'bg-ink-600 text-cream shadow-md' : 'text-ink-500 hover:text-ink-600'
          }`}
        >
          <FolderKanban className="w-4 h-4" /> Devam Eden Projeler
        </button>
        <button
          onClick={() => setTab('completed')}
          data-testid="tab-completed"
          className={`px-5 h-11 rounded-xl text-sm font-semibold transition-all inline-flex items-center gap-2 ${
            tab === 'completed' ? 'bg-emerald-600 text-white shadow-md' : 'text-ink-500 hover:text-ink-600'
          }`}
        >
          <CheckCircle2 className="w-4 h-4" /> Tamamlanan Projeler
        </button>
      </div>

      {tab === 'ongoing' ? (
        <ContentEditor
          key="ongoing"
          name="ongoing_projects"
          kicker=""
          title=""
          hideHeader
          schema={ONGOING_SCHEMA}
          defaultData={{ title: '', category: '', year: '2026', date: '', image: '', excerpt: '', video_id: '', type: 'image', content: '', featured: false }}
        />
      ) : (
        <ContentEditor
          key="completed"
          name="completed_projects"
          kicker=""
          title=""
          hideHeader
          schema={COMPLETED_SCHEMA}
          defaultData={{ title: '', category: '', year: '2023', date: '', image: '', excerpt: '', beneficiaries: '', budget: '', video_id: '', type: 'image', content: '' }}
        />
      )}
    </div>
  );
}
